import { useMemo, useState } from 'react';
import { Button, Card, Form, ListGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { canAccessModule } from '../utils/permissionUtils';
import HelpPane from './HelpPane';
import { getHelpContent } from './helpContent';
import { searchHelp } from '../assistant/searchHelp';

/**
 * Help & Support: type a few words ("tally export", "bank statement") and open the help topic about it, with where the screen is.
 * Where there is an assistant (Account Management > Search & Ask), onAsk(words) offers to take the same words to it.
 */
function HelpSearch({ onAsk, partner = false }) {
  const [query, setQuery] = useState('');
  const [openKey, setOpenKey] = useState(null);
  const results = useMemo(() => searchHelp(query, 8, { partner }), [query, partner]);
  const topic = openKey ? getHelpContent(openKey) : null;
  const { user } = useAuth();
  const navigate = useNavigate();
  // Only the screens this person can open: a link must never lead to a menu they do not have
  const links = (openKey ? results.find((r) => r.key === openKey)?.links || [] : []).filter((l) => canAccessModule(user, l.module));
  const openScreen = (to) => {
    setOpenKey(null);
    navigate(to);
  };

  return (
    <Card className="mb-4">
      <Card.Body>
        <h5><i className="bi bi-search me-2" />Where do I find...?</h5>
        <p className="text-muted small mb-2">Type a few words, like <em>tally export</em>, <em>bank statement</em> or <em>quotation</em>.</p>
        <Form.Control
          type="search"
          value={query}
          placeholder="Search the help"
          aria-label="Search the help"
          onChange={(e) => setQuery(e.target.value)}
        />
        {query.trim().length > 1 && (
          results.length === 0 ? (
            <p className="text-muted small mt-3 mb-0">
              Nothing in the help matches. Try other words
              {onAsk ? ', or ask the assistant below.' : ', or send us your question below.'}
            </p>
          ) : (
            <ListGroup variant="flush" className="mt-2">
              {results.map((r) => (
                <ListGroup.Item key={r.key} action onClick={() => setOpenKey(r.key)} className="px-1">
                  <div className="fw-semibold">{r.title}</div>
                  {r.where.length > 0 && <div className="small text-primary">{r.where.join(' or ')}</div>}
                  <div className="small text-muted">{r.snippet}</div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )
        )}
        {onAsk && query.trim().length > 1 && results.length > 0 && (
          <div className="small mt-2">
            Not what you need? <Button variant="link" size="sm" className="p-0 align-baseline" onClick={() => onAsk(query.trim())}>Ask the assistant instead</Button>
          </div>
        )}
      </Card.Body>
      {topic && (
        <HelpPane show onHide={() => setOpenKey(null)} title={topic.title}>
          {links.length > 0 && (
            <div className="d-grid gap-2 mb-3">
              {links.map((l) => (
                <Button key={l.to} variant="primary" onClick={() => openScreen(l.to)}>
                  Open {l.label} <i className="bi bi-arrow-right ms-1" />
                </Button>
              ))}
            </div>
          )}
          {topic.content}
        </HelpPane>
      )}
    </Card>
  );
}

export default HelpSearch;
